<template>
  <div class="tab-container">
    <app-cells>
      <app-input
        v-model="search_filter"
        class="input--300"
        name="search"
        placeholder="Search"
        type="search"
        @input.native="onAdvanceSearch"
      />
    </app-cells>
    <app-spinner v-if="!page_loaded"/>
    <template v-else>
      <v-client-table
        :class="{ 'table-default--loading': table_preloader }"
        :columns="tableColumns"
        :data="table.items"
        :options="tableOptions"
        class="table-default"
      >
        <template #h__employee>
          <div class="table-default__left">Employee</div>
        </template>
        <template #h__position>
          <div class="table-default__left">Position</div>
        </template>
        <template #h__payroll>
          <div class="table-default__left">Payroll</div>
        </template>
        <template #status="{ row }">
          <span :style="{ backgroundColor: row.status.color }" class="status-tag">
            {{ row.status.name }}
          </span>
        </template>
        <template #employee="{ row }">
          <div class="table-default__left">
            <router-link
              :to="{ name: 'employees-profile', params: { id: row.employee.id } }"
              class="table-default__link-border"
            >
              {{ row.employee.full_name }}
            </router-link>
          </div>
        </template>
        <template #position="{ row }">
          <div class="table-default__left">{{ row.employee.position }}</div>
        </template>
        <template #bank_account="{ row }">
          <div class="table-default__left table-default__bank-account-cell">
            <span>{{ bankAccountLabel(row.employee) }}</span>
            <button
              v-if="row.employee.bank_account"
              type="button"
              class="table-default__bank-account-copy-button"
              title="Copy bank account"
              aria-label="Copy bank account"
              @click="copyBankAccount(row.employee.bank_account)"
            ><img src="@/assets/img/copy-account.svg" alt=""></button>
          </div>
        </template>
        <template #amount="{ row }">{{ formatAmount(row.amount) }}</template>
        <template #net_pay_percent_after="{ row }">{{ row.net_pay_percent_after }}%</template>
        <template #employee_notes="{ row }">
          <div class="table-default__controls">
            <div
              v-tooltip="{ content: row.employee_notes, placement: 'right', offset: 10 }"
              class="table-default__activity table-default__activity--notes"
              :class="{'table-default__activity--active': row.employee_notes}"
            ></div>
          </div>
        </template>
        <template #rejection_reason="{ row }">{{ row.rejection_reason || '-' }}</template>
        <template #requested_at="{ row }">{{ formatDateTime(row.created_at) }}</template>
        <template #created_at="{ row }">{{ formatDateTime(row.created_at) }}</template>
        <template #disbursed_at="{ row }">{{ formatDateTime(row.disbursed_at) }}</template>
        <template #rejected_at="{ row }">{{ formatDateTime(row.rejected_at) }}</template>
        <template #repaid_at="{ row }">{{ formatDateTime(row.repaid_at) }}</template>
        <template #apply_in_payroll="{ row }">{{ formatMonth(row.apply_in_payroll) }}</template>
        <template #payroll="{ row }">
          <div class="table-default__left">
            <router-link
              v-if="getPayroll(row)"
              :to="{ name: 'employees-payroll-employees', params: { id: getPayroll(row).id } }"
              class="table-default__link-border"
            >
              {{ formatMonth(getPayroll(row).month) }}
            </router-link>
            <span v-else>-</span>
          </div>
        </template>
        <template #actions="{ row }">
          <div class="table-default__controls">
            <app-button
              v-for="action in getActions(row)"
              :key="action.code"
              :theme="action.theme"
              size="small"
              @click.native.stop="runAction(row, action)"
            >
              {{ action.label }}
            </app-button>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :clickHandler="onClickPagination"
            :container-class="'pagination'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :next-text="'>'"
            :page-class="'pagination__item'"
            :page-count="pagination.pages"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :prev-link-class="'pagination__link pagination__link--prev'"
            :prev-text="'<'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page"/>
      </app-cells>
    </template>
  </div>
</template>

<script>
import { getEmployeesAdvances } from '@/http'
import debounce from 'lodash/debounce'
import { format, parseISO } from 'date-fns'

const tabConfigurations = {
  'employees-advances': {
    category: 'requests',
    columns: ['status', 'requested_at', 'employee', 'position', 'amount', 'bank_account', 'net_pay_percent_after', 'employee_notes', 'created_at', 'actions'],
    headings: {
      status: 'Status',
      requested_at: 'Requested at',
      employee: 'Employee',
      position: 'Position',
      amount: 'Amount',
      bank_account: 'Bank account',
      net_pay_percent_after: 'Net pay after',
      employee_notes: 'Employee notes',
      created_at: 'Created at',
      actions: 'Actions',
    },
  },
  'employees-advances-disbursed': {
    category: 'disbursed',
    columns: ['status', 'disbursed_at', 'employee', 'position', 'amount', 'bank_account', 'apply_in_payroll', 'net_pay_percent_after'],
    headings: {
      status: 'Status',
      disbursed_at: 'Disbursed at',
      employee: 'Employee',
      position: 'Position',
      amount: 'Amount',
      bank_account: 'Bank account',
      apply_in_payroll: 'Apply in payroll',
      net_pay_percent_after: 'Net pay after',
    },
  },
  'employees-advances-rejected': {
    category: 'rejected',
    columns: ['status', 'rejected_at', 'employee', 'position', 'amount', 'bank_account', 'rejection_reason', 'net_pay_percent_after', 'created_at'],
    headings: {
      status: 'Status',
      rejected_at: 'Rejected at',
      employee: 'Employee',
      position: 'Position',
      amount: 'Amount',
      bank_account: 'Bank account',
      rejection_reason: 'Reason',
      net_pay_percent_after: 'Net pay after',
      created_at: 'Created at',
    },
  },
  'employees-advances-archive': {
    category: 'archive',
    columns: ['payroll', 'status', 'repaid_at', 'employee', 'position', 'amount', 'bank_account', 'apply_in_payroll', 'created_at'],
    headings: {
      payroll: 'Payroll',
      status: 'Status',
      repaid_at: 'Repaid at',
      employee: 'Employee',
      position: 'Position',
      amount: 'Amount',
      bank_account: 'Bank account',
      apply_in_payroll: 'Apply in payroll',
      created_at: 'Created at',
    },
  },
}

export default {
  name: 'AdvancesRequests',
  data() {
    return {
      search_filter: '',
      table: {
        items: [],
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 0,
      },
      table_preloader: false,
      page_loaded: false,
      requestActions: [
        {code: 'reject', label: 'Reject', theme: 'error'},
        {code: 'disburse', label: 'Disburse', theme: 'default'},
      ],
    }
  },
  computed: {
    tabConfiguration() {
      return tabConfigurations[this.$route.name] || tabConfigurations['employees-advances']
    },
    tableColumns() {
      return this.tabConfiguration.columns
    },
    tableOptions() {
      return {
        headings: this.tabConfiguration.headings,
        pagination: {show: false},
        sortable: [],
        perPage: 50,
        texts: {
          noResults: 'No matching entries',
        },
        rowClassCallback: () => 'table-default__row',
        resizableColumns: false,
      }
    },
  },
  watch: {
    '$route.name'() {
      this.search_filter = ''
      this.pagination.page = 1
      this.page_loaded = false
      this.fetchItems()
    },
  },
  created() {
    this.fetchItems()
  },
  methods: {
    fetchItems() {
      this.table_preloader = true
      return getEmployeesAdvances({
        page: this.pagination.page,
        page_size: 50,
        search: this.search_filter,
        category: this.tabConfiguration.category,
      })
        .then(response => {
          this.table.items = response.data.results || []
          this.pagination.pages = response.data.pages || 1
          this.pagination.count = response.data.count || 0
        })
        .finally(() => {
          this.table_preloader = false
          this.page_loaded = true
        })
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    onAdvanceSearch() {
      this.searchAdvance(this)
    },
    searchAdvance: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
    runAction(row, action) {
      this.$emit('confirm-action', {row, action})
    },
    getActions(row) {
      if (row.status.code === 'approved') {
        return this.requestActions.filter(action => action.code === 'disburse')
      }
      return this.requestActions
    },
    getPayroll(row) {
      return row.repayment && row.repayment.payroll
    },
    formatAmount(amount) {
      return `${Number(amount || 0).toLocaleString()} TSh`
    },
    formatDateTime(date) {
      if (!date) return '-'
      return format(parseISO(date), 'dd MMMM yyyy HH:mm')
    },
    formatMonth(date) {
      if (!date) return '-'
      return format(parseISO(date), 'MMM yyyy')
    },
    bankAccountLabel(employee) {
      if (!employee.bank_account) return '-'
      const bank = employee.bank && (employee.bank.short_name || employee.bank.name)
      return bank ? `${bank}: ${employee.bank_account}` : employee.bank_account
    },
    copyBankAccount(account) {
      navigator.clipboard.writeText(account).then(() => {
        this.$notify({type: 'success', title: 'Copied to clipboard'})
      })
    },
  }
}
</script>
